// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { NavLink, Redirect, Route, Switch, withRouter } from 'react-router-dom';

import {
  deleteNPSite as deleteNPSiteAction,
  getNPServerStatus as getNPServerStatusAction,
  getSites as getSitesAction,
  setNPSiteEnabled as setNPSiteEnabledAction,
} from 'state/commands/netPublishCommands';
import { getSites, getNPServerStatus } from 'state/selectors';
import type { Dispatch, NetPublishSite } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import StaticTextField from 'view/Forms/StaticTextField';
import LabeledField from 'view/Forms/LabeledField';

type Props = {
  deleteNPSiteAction: typeof deleteNPSiteAction,
  intl: IntlShape,
  getSitesAction: typeof getSitesAction,
  getNPServerStatusAction: typeof getNPServerStatusAction,
  setNPSiteEnabledAction: typeof setNPSiteEnabledAction,
  sites: NetPublishSite[],
  serverStatus: Object,
};

class NetPublishPage extends Component<Props> {
  handleDeleteSite: Function;
  handleSetEnabled: Function;
  renderNoSites: Function;

  constructor() {
    super();
    this.handleDeleteSite = this.handleDeleteSite.bind(this);
    this.handleSetEnabled = this.handleSetEnabled.bind(this);
    this.renderNoSites = this.renderNoSites.bind(this);
  }

  componentDidMount() {
    this.props.getSitesAction();
    this.props.getNPServerStatusAction();
  }

  handleSetEnabled(siteId: string, enabled: boolean) {
    this.props.setNPSiteEnabledAction(siteId, enabled);
  }

  handleDeleteSite(siteId: string) {
    this.props.deleteNPSiteAction(siteId);
  }

  renderNoSites() {
    const {
      intl: { formatMessage },
    } = this.props;
    return (
      <div className="page">
        <div className="page-header">
          {formatMessage(localizedStrings.NP_SITES)}
        </div>
        <div className="page-contents full-page-message">
          <div className="column is-two-thirds">
            <FormattedMessage {...localizedStrings.NO_SITES_EXIST_1} />
            <br />
            <br />
            <FormattedMessage {...localizedStrings.NO_SITES_EXIST_2} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      intl,
      intl: { formatMessage },
      sites,
      serverStatus,
    } = this.props;
    if (sites.length === 0) {
      return this.renderNoSites();
    }
    return (
      <div className="page">
        <div className="page-contents columns is-gapless">
          <div className="column is-one-third page">
            <div className="page-header">
              {formatMessage(localizedStrings.NP_SITES)}
            </div>
            <div className="page bordered-page-column is-scrollable">
              <table className="table is-fullwidth is-hoverable">
                <tbody>
                  {sites.map(site => (
                    <tr key={site.siteId}>
                      <td className="no-padding">
                        <NavLink
                          className="list-item nav-item-list"
                          data-test-name={`np-nav-item-${site.siteId}`}
                          activeClassName="nav-item-list-selected"
                          to={`/netPublish/${site.siteId}`}
                        >
                          <span>{site.siteName}</span>
                        </NavLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="column is-two-thirds page">
            <div className="page-header" />
            <div className="page page-contents-textured portfolio-form-container">
              <Switch>
                {sites.length && (
                  <Redirect
                    exact
                    from="/netPublish"
                    to={`/netPublish/${sites[0].siteId}`}
                  />
                )}
                {sites.map(site => (
                  <Route
                    key={site.siteId}
                    path={`/netPublish/${site.siteId}`}
                    render={props => (
                      <SiteDetails
                        deleteNPSite={this.handleDeleteSite}
                        intl={intl}
                        site={site}
                        serverStatus={serverStatus}
                        setNPSiteEnabled={this.handleSetEnabled}
                      />
                    )}
                  />
                ))}
              </Switch>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

type SiteProps = {
  deleteNPSite: Function,
  intl: IntlShape,
  site: NetPublishSite,
  serverStatus: Object,
  setNPSiteEnabled: Function,
};

class SiteDetails extends Component<SiteProps> {
  handleDeleteSite: Function;
  handleSetEnabled: Function;

  constructor() {
    super();
    this.handleDeleteSite = this.handleDeleteSite.bind(this);
    this.handleSetEnabled = this.handleSetEnabled.bind(this);
  }

  handleSetEnabled() {
    const { setNPSiteEnabled, site } = this.props;
    setNPSiteEnabled(site.siteId, !site.enabled);
  }

  handleDeleteSite() {
    const { deleteNPSite, site } = this.props;
    deleteNPSite(site.siteId);
  }

  render() {
    const {
      intl: { formatDate, formatMessage, formatTime },
      site,
      serverStatus,
    } = this.props;
    var host = 'http://' + window.location.hostname + ':' + serverStatus.port;
    var location = serverStatus.siteBasePath
      ? site.urlName
      : serverStatus.siteBasePath + '/' + site.urlName;
    const siteUrl = host + location;
    const source = !site.galleryName
      ? site.catalogName
      : `${site.catalogName} > ${site.galleryName}`;
    return (
      <div className="box">
        <StaticTextField
          dataTestName="np-site-name"
          label={formatMessage(localizedStrings.NAME)}
          labelClassName="is-medium"
          value={site.siteName}
          valueClassName="is-medium"
        />

        <LabeledField label={formatMessage(localizedStrings.FIELD_TYPE_URL)}>
          <div style={{ marginTop: '6px', wordBreak: 'break-all' }}>
            <a data-test-name="np-site-url" href={siteUrl} target="_blank">
              {siteUrl}
            </a>
          </div>
        </LabeledField>

        <StaticTextField
          dataTestName="np-site-source"
          label={formatMessage(localizedStrings.NP_SOURCE)}
          value={source}
        />
        <StaticTextField
          dataTestName="np-site-created"
          label={formatMessage(localizedStrings.CREATED_ON_DATE)}
          value={`${formatDate(site.creationDate)} ${formatTime(
            site.creationDate
          )}`}
        />

        <div className="vertical-spacer-large" />

        <LabeledField label="">
          <input
            checked={site.enabled}
            className="is-checkradio"
            data-test-name="np-site-enabled-checkbox"
            id="enabled"
            onChange={this.handleSetEnabled}
            type="checkbox"
            name="enabled"
          />
          <label htmlFor="enabled">
            <FormattedMessage {...localizedStrings.NP_SITE_ENABLED_LABEL} />
          </label>
        </LabeledField>

        <div className="vertical-spacer-large" />

        <LabeledField label="">
          <button
            className="button is-dark is-outlined"
            data-test-name="np-site-delete-button"
            onClick={this.handleDeleteSite}
          >
            <span className="icon">
              <i className="fa fa-lg fa-minus-circle" />
            </span>
            <FormattedMessage {...localizedStrings.NP_SITE_DELETE_LABEL} />
          </button>
        </LabeledField>
      </div>
    );
  }
}

const npPageWrapper = connect(
  state => ({
    sites: getSites(state),
    serverStatus: getNPServerStatus(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteNPSiteAction,
      getNPServerStatusAction,
      getSitesAction,
      setNPSiteEnabledAction,
    })
)(injectIntl(NetPublishPage));

export default withRouter(npPageWrapper);



// WEBPACK FOOTER //
// ./src/view/Pages/NetPublishPage.js